/**
 * @file `Contact` — namespacer for `www.contact.*` covering the
 *       `/contact` route (top-nav item) — CAPTCHA challenge mint +
 *       contact-form submit.
 * @module @labcraft/www/src/client/contact
 *
 * Per the workspace Client-shape rule: `CoreLifecycle` namespacer
 * owned by the parent `Www`. Holds no transport of its own — the
 * parent adopts the shared `HttpTransport` and hands a reference here
 * at construction. Logs and metrics bubble under `www.contact.*`.
 */

import { CoreLifecycle } from '@labcraft/core'
import type { HttpTransport } from './index.ts'
import {
  ChallengeRoute,
  ContactRoute,
  type ChallengeResponse,
  type ContactRequest,
  type ContactResponse,
} from '../shared/routes.ts'

/**
 * Typed namespacer for the `/contact` route's reads + writes.
 */
export class Contact extends CoreLifecycle<{ id?: string }> {
  private readonly http: HttpTransport

  constructor(http: HttpTransport) {
    super({ id: 'contact' })
    this.http = http
  }

  /**
   * `GET /api/contact` — mint a fresh PoW CAPTCHA challenge. Wrapped by
   * `useFetchChallengeMutation` on the contact form's mount path. The
   * server is stateless; the HMAC sig over the tuple is what
   * authenticates the solution at `POST /api/contact` verify time.
   */
  async fetchChallenge(): Promise<ChallengeResponse> {
    return this.http.request(ChallengeRoute)
  }

  /**
   * `POST /api/contact` — submit a contact-form message. Success is
   * HTTP 200 + an empty body; failures throw via the underlying
   * transport (the form re-enables and renders a generic error).
   *
   * @param req - Validated contact-form payload, including a solved
   *   CAPTCHA tuple from `fetchChallenge()`.
   */
  async submit(req: ContactRequest): Promise<ContactResponse> {
    return this.http.request(ContactRoute, { body: req })
  }
}
